/**
 * Spec 081a — service-page text fields in the V2 wizard: draft state, limits, payload builders,
 * client-side validation and 400 `fieldErrors` mapping.
 *
 * The partner portal keeps its own copy of this file (separate builds; keep the two in step).
 */
import type {
  CreateOfferingV2Request,
  MaterialsDetailRowPayload,
  OfferingV2Response,
  UpdateOfferingV2Request,
} from '../../types/offeringsV2';

/** Server limits (design §3). Inputs carry these as maxLength; counters turn red at the limit. */
export const SERVICE_TEXT_LIMITS = {
  headline: 80,
  description: 3000,
  listItems: 20,
  listItem: 120,
  materialsRows: 15,
  materialsLabel: 40,
  materialsValue: 120,
  warrantyDescription: 1000,
  warrantyDaysMin: 1,
  warrantyDaysMax: 10950,
  optionDescription: 300,
  optionMoreDetails: 1500,
  valueDescription: 1000,
} as const;

export interface MaterialsRowDraft {
  label: string;
  labelAr: string;
  value: string;
  valueAr: string;
}

export interface ServicePageDraft {
  headline: string;
  headlineAr: string;
  description: string;
  descriptionAr: string;
  whatsIncluded: string[];
  whatsIncludedAr: string[];
  materialsDetails: MaterialsRowDraft[];
  /** Kept as text so a half-typed number survives; parsed on save. */
  warrantyDays: string;
  warrantyDescription: string;
  warrantyDescriptionAr: string;
  /** warrantyDays as loaded from the server (null = never set); decides whether emptying sends 0 (clear). */
  initialWarrantyDays: number | null;
}

/** Field errors keyed like the server's 400 `errors` map: `headline`, `whatsIncluded[3]`, `variations[0].description`. */
export type FieldErrors = Record<string, string>;

export const emptyMaterialsRow = (): MaterialsRowDraft => ({ label: '', labelAr: '', value: '', valueAr: '' });

export const emptyServicePageDraft = (): ServicePageDraft => ({
  headline: '',
  headlineAr: '',
  description: '',
  descriptionAr: '',
  whatsIncluded: [],
  whatsIncludedAr: [],
  materialsDetails: [],
  warrantyDays: '',
  warrantyDescription: '',
  warrantyDescriptionAr: '',
  initialWarrantyDays: null,
});

export function servicePageDraftFrom(off: OfferingV2Response): ServicePageDraft {
  return {
    headline: off.headline ?? '',
    headlineAr: off.headlineAr ?? '',
    description: off.description ?? '',
    descriptionAr: off.descriptionAr ?? '',
    whatsIncluded: [...(off.whatsIncluded ?? [])],
    whatsIncludedAr: [...(off.whatsIncludedAr ?? [])],
    materialsDetails: (off.materialsDetails ?? []).map((r) => ({
      label: r.label ?? '',
      labelAr: r.labelAr ?? '',
      value: r.value ?? '',
      valueAr: r.valueAr ?? '',
    })),
    warrantyDays: off.warrantyDays != null ? String(off.warrantyDays) : '',
    warrantyDescription: off.warrantyDescription ?? '',
    warrantyDescriptionAr: off.warrantyDescriptionAr ?? '',
    initialWarrantyDays: off.warrantyDays ?? null,
  };
}

const trimOrUndefined = (s: string | undefined | null): string | undefined => {
  const t = (s ?? '').trim();
  return t ? t : undefined;
};

const cleanList = (items: string[]): string[] => items.map((i) => i.trim()).filter(Boolean);

const cleanRows = (rows: MaterialsRowDraft[]): MaterialsDetailRowPayload[] =>
  rows
    .map((r) => ({
      label: trimOrUndefined(r.label),
      labelAr: trimOrUndefined(r.labelAr),
      value: trimOrUndefined(r.value),
      valueAr: trimOrUndefined(r.valueAr),
    }))
    .filter((r) => r.label || r.labelAr || r.value || r.valueAr);

/** null when empty; NaN-safe (validation rejects anything that is not a whole number in range). */
export const parseWarrantyDays = (text: string): number | null => {
  const t = text.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isInteger(n) ? n : null;
};

type ServicePageCreateFields = Pick<CreateOfferingV2Request,
  'headline' | 'headlineAr' | 'description' | 'descriptionAr' | 'whatsIncluded' | 'whatsIncludedAr'
  | 'materialsDetails' | 'warrantyDays' | 'warrantyDescription' | 'warrantyDescriptionAr'>;

/** Create body: blanks are omitted, lists are trimmed and blank lines dropped. */
export function servicePageCreateFields(d: ServicePageDraft): ServicePageCreateFields {
  return {
    headline: trimOrUndefined(d.headline),
    headlineAr: trimOrUndefined(d.headlineAr),
    description: d.description.trim(),
    descriptionAr: trimOrUndefined(d.descriptionAr),
    whatsIncluded: cleanList(d.whatsIncluded),
    whatsIncludedAr: cleanList(d.whatsIncludedAr),
    materialsDetails: cleanRows(d.materialsDetails),
    // Empty on create = omitted (never set).
    warrantyDays: parseWarrantyDays(d.warrantyDays) ?? undefined,
    warrantyDescription: trimOrUndefined(d.warrantyDescription),
    warrantyDescriptionAr: trimOrUndefined(d.warrantyDescriptionAr),
  };
}

type ServicePageUpdateFields = Pick<UpdateOfferingV2Request,
  'headline' | 'headlineAr' | 'description' | 'descriptionAr' | 'whatsIncluded' | 'whatsIncludedAr'
  | 'materialsDetails' | 'warrantyDays' | 'warrantyDescription' | 'warrantyDescriptionAr'>;

/**
 * PUT body: every text field is sent ("" clears it; the server stores null) and every list is sent
 * ([] clears it). warrantyDays: a number sets it; `0` clears it (only sent when the loaded offering
 * had a value and the field was emptied); absent leaves it unchanged.
 */
export function servicePageUpdateFields(d: ServicePageDraft): ServicePageUpdateFields {
  const typed = parseWarrantyDays(d.warrantyDays);
  const days = typed != null ? typed : (d.initialWarrantyDays != null ? 0 : null);
  return {
    headline: d.headline.trim(),
    headlineAr: d.headlineAr.trim(),
    description: d.description.trim(),
    descriptionAr: d.descriptionAr.trim(),
    whatsIncluded: cleanList(d.whatsIncluded),
    whatsIncludedAr: cleanList(d.whatsIncludedAr),
    materialsDetails: cleanRows(d.materialsDetails),
    ...(days != null ? { warrantyDays: days } : {}),
    warrantyDescription: d.warrantyDescription.trim(),
    warrantyDescriptionAr: d.warrantyDescriptionAr.trim(),
  };
}

/** Client-side checks that maxLength cannot enforce. Keys match the server's. */
export function validateServicePage(d: ServicePageDraft): FieldErrors {
  const errors: FieldErrors = {};
  if (!d.description.trim()) errors.description = 'Enter a description of the service.';
  const daysText = d.warrantyDays.trim();
  if (daysText) {
    const n = Number(daysText);
    if (!Number.isInteger(n) || n < SERVICE_TEXT_LIMITS.warrantyDaysMin || n > SERVICE_TEXT_LIMITS.warrantyDaysMax) {
      errors.warrantyDays = `Enter a whole number of days from ${SERVICE_TEXT_LIMITS.warrantyDaysMin} to ${SERVICE_TEXT_LIMITS.warrantyDaysMax}.`;
    }
  }
  if (cleanList(d.whatsIncluded).length > SERVICE_TEXT_LIMITS.listItems) errors.whatsIncluded = `Up to ${SERVICE_TEXT_LIMITS.listItems} items.`;
  if (cleanList(d.whatsIncludedAr).length > SERVICE_TEXT_LIMITS.listItems) errors.whatsIncludedAr = `Up to ${SERVICE_TEXT_LIMITS.listItems} items.`;
  if (cleanRows(d.materialsDetails).length > SERVICE_TEXT_LIMITS.materialsRows) errors.materialsDetails = `Up to ${SERVICE_TEXT_LIMITS.materialsRows} rows.`;
  return errors;
}

/**
 * Reads the 400 `errors` map off an ApiError-shaped value (duck-typed, like isConcurrentUpdateError).
 * Bean Validation container paths (`whatsIncluded[3].<list element>`) are normalised to `whatsIncluded[3]`.
 */
export function fieldErrorsOf(err: unknown): FieldErrors | null {
  if (!err || typeof err !== 'object') return null;
  // Partner ApiError names the map `fieldErrors`; admin ApiError names it `errors`.
  const e = err as { status?: unknown; fieldErrors?: unknown; errors?: unknown };
  const map = e.fieldErrors ?? e.errors;
  if (e.status !== 400 || !map || typeof map !== 'object') return null;
  const out: FieldErrors = {};
  for (const [key, msg] of Object.entries(map as Record<string, unknown>)) {
    out[key.replace(/\.<[^>]+>/g, '')] = String(msg);
  }
  return Object.keys(out).length > 0 ? out : null;
}

const STEP1_KEYS = /^(name|categoryId|subcategoryId|headline|headlineAr|description|descriptionAr|whatsIncluded|whatsIncludedAr|materialsDetails|warrantyDays|warrantyDescription|warrantyDescriptionAr)(\[|\.|$)/;

/** True when any error belongs to a step-1 (Basics) field, so the wizard should jump back to it. */
export const hasStep1Error = (errors: FieldErrors): boolean => Object.keys(errors).some((k) => STEP1_KEYS.test(k));

/**
 * The server indexes `variations[n]` by position in the request body, which (car-specific path) is the
 * pruned pool. Re-key those errors to the wizard's own pool index so they land on the right card.
 */
export function remapVariationErrorKeys(errors: FieldErrors, payloadToPoolIdx: number[]): FieldErrors {
  const out: FieldErrors = {};
  for (const [key, msg] of Object.entries(errors)) {
    const m = key.match(/^variations\[(\d+)\](.*)$/);
    if (m) {
      const poolIdx = payloadToPoolIdx[Number(m[1])];
      out[poolIdx != null ? `variations[${poolIdx}]${m[2]}` : key] = msg;
    } else {
      out[key] = msg;
    }
  }
  return out;
}

/** Errors under a key prefix, e.g. every `whatsIncluded[n]` for the list editor. */
export const hasErrorUnder = (errors: FieldErrors, prefix: string): boolean =>
  Object.keys(errors).some((k) => k === prefix || k.startsWith(`${prefix}[`) || k.startsWith(`${prefix}.`));

const INLINE_KEYS = /^(headline|headlineAr|description|descriptionAr|whatsIncluded|whatsIncludedAr|materialsDetails|warrantyDays|warrantyDescription|warrantyDescriptionAr)(\[|\.|$)|^variations\[\d+\]\.(description|descriptionAr|moreDetails|moreDetailsAr|values\[\d+\]\.(description|descriptionAr))$/;

/** True when every error has an input in the wizard that shows it; otherwise the banner shows the raw message. */
export const allErrorsInline = (errors: FieldErrors): boolean => Object.keys(errors).every((k) => INLINE_KEYS.test(k));

/** Drops the errors of the fields being edited (exact key, or `key[...]` / `key.…` beneath it). */
export function clearErrorsFor(errors: FieldErrors, keys: string[]): FieldErrors {
  const out: FieldErrors = {};
  let changed = false;
  for (const [k, msg] of Object.entries(errors)) {
    if (keys.some((key) => k === key || k.startsWith(`${key}[`) || k.startsWith(`${key}.`))) { changed = true; continue; }
    out[k] = msg;
  }
  return changed ? out : errors;
}

const fmtMinutes = (minutes: number): string => {
  const d = Math.floor(minutes / 1440);
  const h = Math.floor((minutes % 1440) / 60);
  const m = minutes % 60;
  const parts: string[] = [];
  if (d) parts.push(`${d} d`);
  if (h) parts.push(`${h} h`);
  if (m && !d) parts.push(`${m} min`);
  return parts.join(' ') || '0 min';
};

/** "4 h" or "2 h – 1 d 4 h"; null when no duration is known yet. */
export const formatDurationRange = (range: { min: number; max: number } | null | undefined): string | null =>
  !range ? null : range.min === range.max ? fmtMinutes(range.min) : `${fmtMinutes(range.min)} – ${fmtMinutes(range.max)}`;

/**
 * Design §2b duration rule, mirrored client-side: each priced row lasts `override ?? flat`; the range is
 * min–max over the rows. With no rows: `flat ?? legacy` (the offering's durationMinutes). Null when
 * nothing resolves (the UI then says "Set in Cars & Pricing").
 */
export function durationRange(
  rowOverrides: (number | null)[],
  flatMinutes: number | null,
  legacyMinutes: number | null,
): { min: number; max: number } | null {
  const pos = (n: number | null | undefined): n is number => n != null && n > 0;
  if (rowOverrides.length === 0) {
    const m = pos(flatMinutes) ? flatMinutes : (pos(legacyMinutes) ? legacyMinutes : null);
    return m != null ? { min: m, max: m } : null;
  }
  const values = rowOverrides.map((o) => (pos(o) ? o : flatMinutes)).filter(pos);
  return values.length > 0 ? { min: Math.min(...values), max: Math.max(...values) } : null;
}
